import _ from 'lodash';
import { ALL_SELECT_OPTION, DOSSIER_STATUSES, DOSSIER_TYPE_OPTIONS } from '../../constants/settings.constants';
import { userToString } from '../../lib/auth.utils';
import { DOSSIERS_ADVANCED_SEARCH_RESOURCE } from '../../resources/advanced-search/dossiers.advanced.search.resource';
import { DOSSIERS_RESOURCE } from '../../resources/dossiers.resource';
import adminSchoolsService from '../../services/api/admin.schools.service';
import contactsService from '../../services/api/contacts.service';
import partnersService from '../../services/api/partners.service';
import themesService from '../../services/api/themes.service';
import { useAdvancedSearchBase } from '../useAdvancedSearchBase.hook';
import { useDependency } from '../useDependency.hook';

const SEARCH_FIELDS = DOSSIERS_RESOURCE.table.searchParams?.filter((p) => p !== 'query');
const DATE_FIELDS = ['startDateFrom', 'startDateTo', 'deadlineFrom', 'deadlineTo'];
const DEFAULT_FILTERS_STATE = {
  school: [ALL_SELECT_OPTION.value],
  branch: [ALL_SELECT_OPTION.value],
  status: [ALL_SELECT_OPTION.value],
  type: [ALL_SELECT_OPTION.value],
  theme: [ALL_SELECT_OPTION.value],
  requestedBy: [ALL_SELECT_OPTION.value],
  stakeholders: [ALL_SELECT_OPTION.value],
  stakeholdersPartners: [ALL_SELECT_OPTION.value],
  assigned: [ALL_SELECT_OPTION.value]
};
const dataResource = DOSSIERS_RESOURCE;
const filtersResource = DOSSIERS_ADVANCED_SEARCH_RESOURCE;

export const useDossiersAdvancedSearch = () => {
  const advancedSearch = useAdvancedSearchBase(filtersResource, dataResource, {
    searchFields: SEARCH_FIELDS,
    dateFields: DATE_FIELDS,
    defaultFiltersState: DEFAULT_FILTERS_STATE
  });
  const themes = useDependency(themesService.getAll, {
    mapResponse: (resp) => resp?._embedded?.themes?.map((t) => ({ value: t.name, label: t.name }))
  });
  const contacts = useDependency(contactsService.getContactsWithoutParents, {
    mapResponse: (resp) =>
      resp?._embedded?.contacts?.result?.map((i) => ({ ...i, value: i.id, label: userToString(i?.spoContactData) }))
  });
  const organizations = useDependency(partnersService.getPartnersOptions);
  const schools = useDependency(adminSchoolsService.getAllSchools, {
    mapResponse: (items) => items?.map((i) => ({ ...i, value: i.id, label: i.name }))
  });
  const branches = {
    isLoading: schools.isLoading,
    loadError: schools.loadError,
    options: _.flatten(
      schools.options
        ?.filter((s) => {
          if (
            advancedSearch?.filters?.school?.length > 1 ||
            advancedSearch?.filters?.school?.[0] !== ALL_SELECT_OPTION.value
          ) {
            return advancedSearch?.filters?.school?.includes(s.id);
          }
          return true;
        })
        ?.map(
          (s) =>
            s?.branches?.map((b) => ({
              ...b,
              value: b.id,
              label: b.name,
              schoolId: s.id,
              communityId: s.communityId
            })) ?? []
        )
    )
  };
  const dependencies = {
    schools: schools,
    branches: branches,
    statuses: {
      isLoading: false,
      options: _.uniqBy(Object.values(DOSSIER_STATUSES), (s) => s.name).map((status) => ({
        value: status.name,
        label: status.name
      }))
    },
    types: { isLoading: false, options: DOSSIER_TYPE_OPTIONS },
    themes,
    contacts,
    organizations
  };

  const onInput = (value, field) => {
    advancedSearch.onInput(value, field, field === 'school' ? { branch: [ALL_SELECT_OPTION.value] } : {});
  };

  const fetchDependencies = () => {
    themes.onLoad();
    contacts.onLoad();
    organizations.onLoad();
    schools.onLoad();
  };

  const onOpen = () => {
    if (!advancedSearch?.isOpen) {
      fetchDependencies();
    }
    advancedSearch?.onOpen();
  };

  return {
    ...advancedSearch,
    onInput,
    onOpen,
    dependencies,
    fetchDependencies,
    simpleSearchKey: 'query',
    resource: filtersResource,
    dataResource
  };
};
